import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { VideoCameraSlashIcon } from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import { useEmployeeStatus } from '../hooks/useEmployeeStatus';

interface Employee {
  id: string;
  email: string;
  name: string;
  has_webcam: boolean;
  webcam_last_checked: string;
  isOnline: boolean;
}

interface EmployeeStatus {
  id: string;
  isOnline: boolean;
}

interface WebRTCSignal {
  id: string;
  from_user_id: string;
  to_user_id: string;
  type: string;
  payload: any;
}

const WebcamMonitor: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { employeeStatuses, loading: employeesLoading } = useEmployeeStatus();
  const [error, setError] = useState<string | null>(null);
  const [activeEmployees, setActiveEmployees] = useState<Set<string>>(new Set());
  const peerConnections = useRef<{ [key: string]: RTCPeerConnection }>({});
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const pendingConnections = useRef<{ [key: string]: RTCIceCandidate[] }>({});
  const [processedSignals, setProcessedSignals] = useState<Set<string>>(new Set());

  const setVideoRef = useCallback((employeeId: string, el: HTMLVideoElement | null) => {
    if (videoRefs.current) {
      videoRefs.current[employeeId] = el;
    }
  }, []);

  const createPeerConnection = useCallback((employeeId: string) => {
    console.log('Creando nueva conexión para empleado:', employeeId);
    
    // Limpiar cualquier conexión existente
    const existingPc = peerConnections.current[employeeId];
    if (existingPc) {
      console.log('Estado de conexión existente:', {
        connectionState: existingPc.connectionState,
        iceConnectionState: existingPc.iceConnectionState,
        signalingState: existingPc.signalingState
      });
      
      if (existingPc.connectionState === 'failed' || 
          existingPc.connectionState === 'closed' ||
          existingPc.connectionState === 'disconnected' ||
          existingPc.iceConnectionState === 'failed') {
        console.log('Cerrando conexión problemática');
        existingPc.close();
        delete peerConnections.current[employeeId];
      } else if (existingPc.connectionState === 'connected' && 
                 existingPc.iceConnectionState === 'connected') {
        console.log('Reutilizando conexión estable existente');
        return existingPc;
      } else {
        console.log('Cerrando conexión en estado incierto');
        existingPc.close();
        delete peerConnections.current[employeeId];
      }
    }
    
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    // Manejar streams entrantes
    pc.ontrack = (event) => {
      console.log('Stream recibido para empleado:', employeeId, event.streams[0]);
      const videoElement = videoRefs.current[employeeId];
      if (videoElement && event.streams[0]) {
        console.log('Asignando stream al elemento video');
        videoElement.srcObject = event.streams[0];
      } else {
        console.log('No se pudo asignar stream:', {
          videoElement: !!videoElement,
          stream: !!event.streams[0]
        });
      }
    };

    // Manejar estado de la conexión con más detalle
    pc.onconnectionstatechange = () => {
      console.log(`Estado de conexión para ${employeeId}:`, {
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
        signalingState: pc.signalingState
      });

      if (pc.connectionState === 'failed' || 
          pc.connectionState === 'disconnected' ||
          pc.iceConnectionState === 'failed') {
        console.log('Conexión problemática, reiniciando...');
        if (peerConnections.current[employeeId] === pc) {
          pc.close();
          delete peerConnections.current[employeeId];
          // Crear nueva conexión después de un breve delay
          setTimeout(() => createPeerConnection(employeeId), 1000);
        }
      }
    };

    // Manejar candidatos ICE
    pc.onicecandidate = async (event) => {
      if (event.candidate) {
        console.log('Enviando candidato ICE al empleado:', employeeId, {
          candidateType: event.candidate.type,
          candidateProtocol: event.candidate.protocol
        });
        
        const signalId = crypto.randomUUID();
        const { error } = await supabase
          .from('webrtc_signaling')
          .insert({
            id: signalId,
            from_user_id: user?.id,
            to_user_id: employeeId,
            type: 'ice-candidate',
            payload: event.candidate.toJSON(),
            created_at: new Date().toISOString()
          });

        if (error) {
          console.error('Error al enviar candidato ICE:', error);
        }
      }
    };

    // Guardar la nueva conexión
    peerConnections.current[employeeId] = pc;
    return pc;
  }, [user?.id]);

  const processPendingCandidates = useCallback((employeeId: string) => {
    const pc = peerConnections.current[employeeId];
    const candidates = pendingConnections.current[employeeId] || [];
    
    if (pc && pc.remoteDescription && candidates.length > 0) {
      console.log('Procesando candidatos ICE pendientes para:', employeeId);
      candidates.forEach(async (candidate) => {
        try {
          await pc.addIceCandidate(candidate);
          console.log('Candidato ICE pendiente agregado');
        } catch (err) {
          console.error('Error al agregar candidato ICE pendiente:', err);
        }
      });
      pendingConnections.current[employeeId] = [];
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        if (profile?.role !== 'supervisor') {
          navigate('/dashboard');
          return;
        }
      } catch (err) {
        setError('Error al verificar el rol del usuario');
        console.error(err);
      }
    };

    initialize();
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.id) return;

    const setupConnections = async () => {
      for (const employee of employeeStatuses) {
        if (employee.isOnline) {
          console.log('Configurando conexión para empleado:', employee.id);
          
          const existingPc = peerConnections.current[employee.id];
          const needsNewConnection = !existingPc || 
                                   existingPc.connectionState === 'failed' || 
                                   existingPc.connectionState === 'closed' ||
                                   existingPc.connectionState === 'disconnected' ||
                                   existingPc.iceConnectionState === 'failed';

          if (needsNewConnection) {
            console.log('Iniciando nueva conexión');
            const pc = createPeerConnection(employee.id);
            
            try {
              console.log('Creando oferta para empleado:', employee.id);
              const offer = await pc.createOffer({
                offerToReceiveVideo: true,
                offerToReceiveAudio: false
              });
              
              console.log('Estableciendo descripción local');
              await pc.setLocalDescription(offer);
              
              console.log('Enviando oferta al empleado');
              const { error } = await supabase
                .from('webrtc_signaling')
                .insert({
                  id: crypto.randomUUID(),
                  from_user_id: user?.id,
                  to_user_id: employee.id,
                  type: 'offer',
                  payload: offer,
                  created_at: new Date().toISOString()
                });

              if (error) {
                console.error('Error al enviar oferta:', error);
              }
            } catch (err) {
              console.error('Error al crear/enviar oferta:', err);
            }
          } else {
            console.log('Usando conexión existente para:', employee.id);
          }
        } else {
          console.log('Empleado desconectado:', employee.id);
          // Limpiar conexión si el empleado está desconectado
          const pc = peerConnections.current[employee.id];
          if (pc) {
            console.log('Cerrando conexión de empleado desconectado');
            pc.close();
            delete peerConnections.current[employee.id];
          }
        }
      }
    };

    setupConnections();
  }, [employeeStatuses, user?.id, createPeerConnection]);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('webrtc-supervisor')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'webrtc_signaling',
          filter: `to_user_id=eq.${user?.id}`
        },
        async (payload) => {
          const signal = payload.new as WebRTCSignal;
          
          // Evitar procesar señales duplicadas
          if (processedSignals.has(signal.id)) {
            console.log('Señal ya procesada, ignorando:', signal.id);
            return;
          }
          setProcessedSignals(prev => {
            const newSet = new Set(prev);
            newSet.add(signal.id);
            return newSet;
          });

          const employeeId = signal.from_user_id;
          let pc = peerConnections.current[employeeId];

          try {
            if (signal.type === 'answer') {
              console.log('Procesando respuesta de empleado:', employeeId);
              
              if (!pc || pc.connectionState === 'failed') {
                console.log('Conexión no encontrada o fallida, ignorando respuesta');
                return;
              }

              if (pc.signalingState !== 'have-local-offer') {
                console.log('Estado de señalización incorrecto:', pc.signalingState);
                return;
              }

              await pc.setRemoteDescription(new RTCSessionDescription(signal.payload));
              console.log('Respuesta establecida para empleado:', employeeId);
              
              // Procesar candidatos ICE pendientes después de establecer la descripción remota
              processPendingCandidates(employeeId);

            } else if (signal.type === 'ice-candidate') {
              console.log('Recibido candidato ICE de empleado:', employeeId);
              const candidate = new RTCIceCandidate(signal.payload);

              if (!pc || pc.connectionState === 'failed') {
                console.log('Conexión no encontrada o fallida, creando nueva conexión');
                pc = createPeerConnection(employeeId);
                
                // Crear y enviar oferta
                const offer = await pc.createOffer({
                  offerToReceiveVideo: true,
                  offerToReceiveAudio: false
                });
                await pc.setLocalDescription(offer);
                
                const signalId = crypto.randomUUID();
                const { error } = await supabase
                  .from('webrtc_signaling')
                  .insert({
                    id: signalId,
                    from_user_id: user.id,
                    to_user_id: employeeId,
                    type: 'offer',
                    payload: offer,
                    created_at: new Date().toISOString()
                  });

                if (error) {
                  console.error('Error al enviar oferta:', error);
                  return;
                }
              }

              if (pc.remoteDescription) {
                await pc.addIceCandidate(candidate);
                console.log('Candidato ICE agregado para empleado:', employeeId);
              } else {
                console.log('Guardando candidato ICE (no hay descripción remota):', employeeId);
                if (!pendingConnections.current[employeeId]) {
                  pendingConnections.current[employeeId] = [];
                }
                pendingConnections.current[employeeId].push(candidate);
              }
            }
          } catch (err) {
            console.error('Error al procesar señal:', err);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, createPeerConnection, processPendingCandidates]);

  useEffect(() => {
    employeeStatuses.forEach(employee => {
      const wasOnline = activeEmployees.has(employee.id);
      const isOnline = employee.isOnline;

      if (wasOnline && !isOnline) {
        console.log('Empleado desconectado, limpiando conexión:', employee.id);
        if (peerConnections.current[employee.id]) {
          peerConnections.current[employee.id].close();
          delete peerConnections.current[employee.id];
        }
        if (videoRefs.current[employee.id]) {
          const videoElement = videoRefs.current[employee.id];
          if (videoElement && videoElement.srcObject) {
            const stream = videoElement.srcObject as MediaStream;
            stream.getTracks().forEach(track => track.stop());
          }
          videoRefs.current[employee.id] = null;
        }
        const newActiveEmployees = new Set(activeEmployees);
        newActiveEmployees.delete(employee.id);
        setActiveEmployees(newActiveEmployees);
      }
    });
  }, [employeeStatuses, activeEmployees]);

  useEffect(() => {
    return () => {
      Object.values(peerConnections.current).forEach(connection => {
        if (connection) {
          connection.close();
        }
      });
      peerConnections.current = {};

      Object.values(videoRefs.current).forEach(videoElement => {
        if (videoElement && videoElement.srcObject) {
          const stream = videoElement.srcObject as MediaStream;
          stream.getTracks().forEach(track => {
            track.stop();
            console.log('Track stopped on cleanup:', track.label);
          });
        }
      });
      videoRefs.current = {};

      setActiveEmployees(new Set());
    };
  }, []);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Monitor de Webcams</h1>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {employeeStatuses.map((employee) => (
            <div
              key={employee.id}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                employee.isOnline ? 'border-2 border-green-500' : 'border border-gray-200'
              }`}
            >
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-2">{employee.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{employee.email}</p>
                <div className="flex items-center space-x-2 mb-2">
                  <span
                    className={`h-3 w-3 rounded-full ${
                      employee.isOnline ? 'bg-green-500' : 'bg-gray-400'
                    }`}
                  />
                  <span className="text-sm">
                    {employee.isOnline ? 'En línea' : 'Desconectado'}
                  </span>
                </div>
              </div>
              <div className="relative aspect-video bg-gray-100">
                {employee.isOnline ? (
                  <video
                    ref={el => setVideoRef(employee.id, el)}
                    className="w-full h-full object-cover"
                    playsInline
                    autoPlay
                    muted
                    style={{ transform: 'rotateY(180deg)' }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <VideoCameraSlashIcon className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default WebcamMonitor;
